/**
 * Tariff Validation Utilities
 *
 * Validates ElectricTariff configuration against backend rules
 * to prevent submission of invalid scenarios.
 *
 * Backend reference: apps/site-dna-api/services/preprocessing_service.py
 * _validate_tariff() function (lines 407-442)
 */

/**
 * Validates ElectricTariff configuration against backend rules
 *
 * @param {Object} electricTariff - The ElectricTariff object from buildScenarioPayload()
 * @param {boolean} offGrid - Whether scenario is off-grid
 * @returns {Object} { valid: boolean, error: string | null }
 */
export function validateElectricTariff(electricTariff, offGrid) {
    // Off-grid scenarios don't require tariff
    if (offGrid) {
        return { valid: true, error: null };
    }

    // On-grid scenarios MUST have ElectricTariff
    if (!electricTariff || typeof electricTariff !== 'object' || Object.keys(electricTariff).length === 0) {
        return {
            valid: false,
            error: 'On-grid scenarios require a tariff selection. Please select a tariff on the Utility tab.'
        };
    }

    // Check for at least one tariff type
    const hasStructuredRate = 'urdb_response' in electricTariff;
    const hasBlended = Object.keys(electricTariff).some(k => k.startsWith('blended'));
    const hasTou = Object.keys(electricTariff).some(k => k.startsWith('tou'));
    const hasCustomSeries = 'custom_electricity_rate_series_kw' in electricTariff;

    // Valid if has at least one tariff definition
    const tariffTypeCount = [hasStructuredRate, hasBlended, hasTou, hasCustomSeries].filter(Boolean).length;

    if (tariffTypeCount === 0) {
        return {
            valid: false,
            error: 'Tariff data is incomplete. Please select a valid tariff on the Utility tab or enter custom rates.'
        };
    }

    // All checks passed
    return { valid: true, error: null };
}

/**
 * Validates tariff selection from form state (before payload build)
 *
 * @param {Object} scenarioForm - useScenarioForm state
 * @returns {Object} { valid: boolean, error: string | null, warning: string | null }
 */
export function validateTariffSelection(scenarioForm) {
    const { offGrid, customTariffData, useManualRate, blendedEnergyRate } = scenarioForm;

    // Off-grid is always valid
    if (offGrid) {
        return { valid: true, error: null, warning: null };
    }

    // Check if any tariff source is configured
    const hasCustomData = customTariffData && Object.keys(customTariffData).length > 0;
    const hasManualRate = useManualRate && blendedEnergyRate != null && !isNaN(parseFloat(blendedEnergyRate));

    if (!hasCustomData && !hasManualRate) {
        return {
            valid: false,
            error: 'On-grid scenarios require a tariff. Please select a utility tariff or enter custom rates on the Utility tab.',
            warning: null
        };
    }

    // Warn if manual rate is suspiciously low/high
    if (hasManualRate) {
        const rate = parseFloat(blendedEnergyRate);
        if (rate < 0.01) {
            return {
                valid: true,
                error: null,
                warning: `Energy rate ($${rate}/kWh) seems unusually low. Typical rates are $0.08-$0.30/kWh.`
            };
        }
        if (rate > 1.0) {
            return {
                valid: true,
                error: null,
                warning: `Energy rate ($${rate}/kWh) seems unusually high. Typical rates are $0.08-$0.30/kWh.`
            };
        }
    }

    return { valid: true, error: null, warning: null };
}
